/**
 * web/src/components/shared/Progress.tsx
 * Segmented progress bar (handoff §Components §Progress; design_files/
 * styles.css lines 826-841). 3px tall, 3px gap. Segment states:
 * done (75% completed green), current (running indigo or waiting amber),
 * failed (red), default (bg-muted).
 *
 * Lifted from design_files/shell.jsx Progress prototype (lines 170-184)
 * and translated to Tailwind utilities consuming the @theme tokens.
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { WorkflowStatus } from '../../api/types';

/** Public props for Progress. */
export interface ProgressProps {
  total: number;
  /** 1-based current step (pass currentStepIndex+1 from server). */
  current: number;
  /** Workflow status, or a refined key: 'rejected' / 'awaiting_reply'. The
   * latter colors the current segment teal to match the sponsor-wait state. */
  status: WorkflowStatus | 'rejected' | 'awaiting_reply';
  className?: string;
}

/**
 * Renders `total` 3px segments. Uses the same coloring rules as
 * design_files/shell.jsx lines 170-184 + styles.css lines 826-841.
 *
 * @param total total number of steps
 * @param current 1-based current step index (so `total === current` means done)
 * @param status workflow status or synthetic 'rejected'
 * @returns a flex row of segments with `role="progressbar"` + aria-valuenow
 */
export function Progress({ total, current, status, className }: ProgressProps) {
  const segments: ReactNode[] = [];
  for (let i = 1; i <= total; i++) {
    let segCls = 'bg-bg-muted';
    if (status === 'completed') {
      segCls = 'bg-completed/75';
    } else if (status === 'failed' && i === current) {
      segCls = 'bg-failed';
    } else if (status === 'failed' && i < current) {
      segCls = 'bg-completed/75';
    } else if (i < current) {
      segCls = 'bg-completed/75';
    } else if (i === current) {
      if (status === 'awaiting_reply') segCls = 'bg-awaiting';
      else if (status === 'paused' || status === 'rejected') segCls = 'bg-waiting';
      else if (status === 'running') segCls = 'bg-running';
    }
    segments.push(
      <div
        key={i}
        data-testid={`progress-seg-${i}`}
        className={cn('flex-1 h-full rounded-[1.5px]', segCls)}
      />,
    );
  }
  return (
    <div
      className={cn('flex gap-[3px] h-[3px]', className)}
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      {segments}
    </div>
  );
}
